<script setup lang="ts">
import { computed } from 'vue'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairCheckboxIndicator } from '@/ui/primitives/FairCheckbox'
import { FairRadioButtonIndicator } from '@/ui/primitives/FairRadioButton'
import type { FairIconName } from '@/ui/tokens/icons.data'
import type { FairInputListItemState } from '@/ui/tokens/inputs.data'

interface FairInputListItemProps {
  label: string
  description?: string
  count?: string
  emoji?: string
  group?: boolean
  treeNode?: boolean
  expanded?: boolean
  ai?: boolean
  disabled?: boolean
  checkbox?: boolean
  checkboxChecked?: boolean
  showCheckbox?: boolean
  /** Single-select indicator (Select / sort). Mutually exclusive with checkbox in UI. */
  radio?: boolean
  radioSelected?: boolean
  showRadio?: boolean
  icon?: FairIconName
  trailingIcon?: FairIconName
  depth?: number
  highlighted?: boolean
  interactive?: boolean
  previewState?: FairInputListItemState
}

const props = withDefaults(defineProps<FairInputListItemProps>(), {
  group: false,
  treeNode: false,
  expanded: false,
  ai: false,
  disabled: false,
  checkbox: false,
  checkboxChecked: false,
  showCheckbox: true,
  radio: false,
  radioSelected: false,
  showRadio: true,
  depth: 0,
  highlighted: false,
  interactive: true,
  previewState: 'default',
})

const emit = defineEmits<{
  select: []
  'toggle-expand': []
  'toggle-checkbox': []
}>()

const rootTag = computed(() => (props.interactive ? 'button' : 'div'))

const rootClass = computed(() => ({
  'fair-input-list-item': true,
  'fair-input-list-item--group': props.group,
  'fair-input-list-item--tree': props.treeNode,
  'fair-input-list-item--interactive': props.interactive,
  'fair-input-list-item--ai': props.ai,
  'fair-input-list-item--disabled': props.disabled || props.previewState === 'disabled',
  'fair-input-list-item--hover': props.previewState === 'hover' || props.highlighted,
  'fair-input-list-item--highlight': props.previewState === 'highlight',
}))

const showCheckboxControl = computed(() => {
  if (props.group || props.radio) {
    return false
  }

  return props.showCheckbox && props.checkbox
})

const showRadioControl = computed(() => {
  if (props.group || props.checkbox) {
    return false
  }

  return props.showRadio && props.radio
})

const checkboxCheckedState = computed(() => {
  return props.checkboxChecked || props.previewState === 'highlight'
})

const radioSelectedState = computed(() => {
  return props.radioSelected || props.previewState === 'highlight'
})

const labelClass = computed(() => ({
  'fair-input-list-item__label fair-text-body-small': true,
  'fair-input-list-item__label--bold': props.group,
  'fair-input-list-item__label--highlight':
    props.checkboxChecked
    || props.radioSelected
    || props.previewState === 'highlight',
}))

const treeIndentStyle = computed(() => ({
  paddingLeft: props.treeNode || props.depth > 0
    ? `calc(var(--fair-padding-s) + ${props.depth * 16}px)`
    : undefined,
}))

function handleRootClick() {
  if (props.disabled) {
    return
  }

  emit('select')
}

function handleExpandClick(event: MouseEvent) {
  event.stopPropagation()
  emit('toggle-expand')
}

function handleCheckboxClick(event: MouseEvent) {
  event.stopPropagation()
  emit('toggle-checkbox')
}
</script>

<template>
  <component
    :is="rootTag"
    :type="interactive ? 'button' : undefined"
    :class="rootClass"
    :style="treeIndentStyle"
    :disabled="interactive && disabled"
    @click="handleRootClick"
  >
    <template v-if="group">
      <div class="fair-input-list-item__group-inner">
        <span v-if="emoji" class="fair-input-list-item__emoji">{{ emoji }}</span>

        <FairIcon
          v-if="icon"
          :name="icon"
          :size="16"
          color="var(--fair-color-primary-blue-100)"
        />

        <div class="fair-input-list-item__body">
          <span :class="labelClass">{{ label }}</span>
          <span
            v-if="description"
            class="fair-input-list-item__description fair-text-caption"
          >
            {{ description }}
          </span>
        </div>

        <span v-if="count" class="fair-input-list-item__count fair-text-body-small">
          {{ count }}
        </span>

        <FairIcon
          v-if="trailingIcon"
          :name="trailingIcon"
          :size="16"
          color="var(--fair-color-primary-blue-80)"
        />
      </div>
    </template>

    <template v-else>
      <button
        v-if="treeNode"
        type="button"
        class="fair-input-list-item__chevron"
        aria-label="Toggle nested items"
        @click="handleExpandClick"
      >
        <FairIcon
          :name="expanded ? 'caret-down' : 'arrow-right'"
          :size="16"
          color="var(--fair-color-primary-blue-80)"
        />
      </button>

      <button
        v-if="showCheckboxControl"
        type="button"
        class="fair-input-list-item__checkbox-host fair-checkbox-indicator-host"
        aria-label="Toggle selection"
        @click="handleCheckboxClick"
      >
        <FairCheckboxIndicator
          :checked="checkboxCheckedState"
          :disabled="disabled || previewState === 'disabled'"
        />
      </button>

      <!-- Visual only — row click selects (avoids nested buttons) -->
      <span
        v-if="showRadioControl"
        class="fair-input-list-item__radio-host fair-radio-button-indicator-host"
        aria-hidden="true"
      >
        <FairRadioButtonIndicator
          :selected="radioSelectedState"
          :disabled="disabled || previewState === 'disabled'"
        />
      </span>

      <span
        v-if="$slots.leading"
        class="fair-input-list-item__leading"
      >
        <slot name="leading" />
      </span>

      <FairIcon
        v-else-if="icon"
        :name="icon"
        :size="16"
        color="var(--fair-color-primary-blue-80)"
      />

      <div class="fair-input-list-item__body">
        <span :class="labelClass">{{ label }}</span>
        <span
          v-if="description"
          class="fair-input-list-item__description fair-text-caption"
        >
          {{ description }}
        </span>
      </div>

      <span v-if="count" class="fair-input-list-item__count fair-text-body-small">
        {{ count }}
      </span>

      <FairIcon
        v-if="trailingIcon"
        :name="trailingIcon"
        :size="16"
        color="var(--fair-color-primary-blue-80)"
      />
    </template>
  </component>
</template>
